import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CITIES = ['Atlanta', 'New York City', 'Los Angeles', 'Philadelphia', 'Toronto', 'Montreal'];

const SignupForm = ({ preselectedCity, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    preferred_date_time: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Update city when preselectedCity changes - with improved browser compatibility
  useEffect(() => {
    if (preselectedCity && preselectedCity !== formData.city) {
      setFormData(prevData => {
        return {
          ...prevData,
          city: preselectedCity
        };
      });
    }
  }, [preselectedCity]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => {
      return {
        ...prevData,
        [name]: value
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await axios.post(`${API}/signup`, formData);
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit signup. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      <div>
        <label htmlFor="name" className="block text-sm font-medium mb-2 uppercase tracking-wider">
          Full Name *
        </label>
        <input
          id="name"
          name="name"
          type="text"
          value={formData.name}
          onChange={handleChange}
          className="underline-input w-full"
          placeholder="Enter your full name"
          required
          disabled={loading}
          data-testid="signup-name-input"
        />
      </div>

      <div>
        <label htmlFor="email" className="block text-sm font-medium mb-2 uppercase tracking-wider">
          Email *
        </label>
        <input
          id="email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          className="underline-input w-full"
          placeholder="your.email@example.com"
          required
          disabled={loading}
          data-testid="signup-email-input"
        />
      </div>

      <div>
        <label htmlFor="phone" className="block text-sm font-medium mb-2 uppercase tracking-wider">
          Phone Number *
        </label>
        <input
          id="phone"
          name="phone"
          type="tel"
          value={formData.phone}
          onChange={handleChange}
          className="underline-input w-full"
          placeholder="(555) 123-4567"
          required
          disabled={loading}
          data-testid="signup-phone-input"
        />
      </div>

      <div>
        <label htmlFor="city" className="block text-sm font-medium mb-2 uppercase tracking-wider">
          City *
        </label>
        <select
          id="city"
          name="city"
          value={formData.city}
          onChange={handleChange}
          className="underline-input w-full bg-white cursor-pointer"
          required
          disabled={loading}
          data-testid="signup-city-select"
        >
          <option value="">Select your city</option>
          {CITIES.map(city => (
            <option key={city} value={city}>{city}</option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="preferred_date_time" className="block text-sm font-medium mb-2 uppercase tracking-wider">
          Preferred Date/Time *
        </label>
        <input
          id="preferred_date_time"
          name="preferred_date_time"
          type="text"
          value={formData.preferred_date_time}
          onChange={handleChange}
          className="underline-input w-full"
          placeholder="e.g., Weekday evenings, Weekend afternoons"
          required
          disabled={loading}
          data-testid="signup-datetime-input"
        />
      </div>

      {error && (
        <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3" data-testid="signup-error">
          <p className="text-sm">{error}</p>
        </div>
      )}

      <button
        type="submit"
        className="btn-primary w-full"
        disabled={loading}
        data-testid="signup-submit-btn"
      >
        {loading ? 'SUBMITTING...' : 'SIGN UP FOR TOUR'}
      </button>

      <p className="text-sm text-muted-foreground text-center">
        By signing up, you'll receive priority access to purchase tickets and updates about tour dates in your city.
      </p>
    </form>
  );
};

export default SignupForm;